/**
 * The "Sign in with Google" button, rendered by Google itself.
 *
 * `google.accounts.id.renderButton` draws it rather than this file styling a
 * lookalike, and that is a requirement rather than laziness: Google's branding
 * terms govern what the button may look like, and a hand-rolled one silently
 * drifts out of compliance and out of date. It also means the click handler,
 * the popup and the FedCM path are Google's, not ours.
 *
 * This component owns exactly one job — turn a click into a `credential`
 * string and hand it upward. It never decodes the token, never reads the email
 * inside it, and never decides who anybody is. That happens server-side, in
 * `backend/api/app/security/google_auth_tokens.py`.
 *
 * If GIS cannot load — ad blocker, offline laptop, corporate proxy — the
 * button reports it through `onUnavailable` so the login page can fall back to
 * the password form instead of showing an empty rectangle.
 */
import { useEffect, useRef, useState } from "react";
import { loadGoogleIdentityServices } from "./google";

export interface GoogleSignInButtonProps {
  clientId: string;
  /** Receives the raw ID token. Opaque here; verified on the server. */
  onCredential: (credential: string) => void;
  /** GIS could not load. The caller should offer another way in. */
  onUnavailable?: (message: string) => void;
  /** Suppresses interaction while the exchange is in flight. */
  disabled?: boolean;
}

export function GoogleSignInButton({
  clientId,
  onCredential,
  onUnavailable,
  disabled = false,
}: GoogleSignInButtonProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = useState(false);

  // The callback is read through a ref so re-rendering the page (a typed
  // character in the password field, say) never re-initialises GIS. GIS keeps
  // the callback it was given at `initialize` time, so re-initialising to pick
  // up a new closure would tear down and redraw the button on every keystroke.
  const onCredentialRef = useRef(onCredential);
  onCredentialRef.current = onCredential;
  const onUnavailableRef = useRef(onUnavailable);
  onUnavailableRef.current = onUnavailable;

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const api = await loadGoogleIdentityServices();
        if (cancelled || !hostRef.current) return;

        api.initialize({
          client_id: clientId,
          callback: (response) => {
            const credential = response?.credential;
            if (typeof credential === "string" && credential) {
              onCredentialRef.current(credential);
            }
          },
          // No One Tap and no auto-select: signing somebody in because they
          // happen to have a Google session open, without them asking, is a
          // surprise on a page that shows the family's finances.
          auto_select: false,
          cancel_on_tap_outside: true,
          itp_support: true,
        });

        // Width must be a NUMBER of pixels — GIS rejects percentages, and a
        // rejected width silently renders a default-width button that does not
        // line up with the form beneath it.
        api.renderButton(hostRef.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: "signin_with",
          shape: "pill",
          logo_alignment: "left",
          width: Math.round(hostRef.current.getBoundingClientRect().width) || 320,
        });
        setReady(true);
      } catch (err) {
        if (cancelled) return;
        onUnavailableRef.current?.(
          err instanceof Error ? err.message : "Google sign-in is unavailable.",
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  return (
    <div className="relative">
      <div
        ref={hostRef}
        className="flex w-full justify-center"
        // `aria-busy` rather than a spinner: the button appears the moment GIS
        // paints it, and a placeholder that swaps for the real thing causes a
        // layout jump on the first screen of the app.
        aria-busy={!ready}
      />
      {disabled && (
        // A transparent cover, because the iframe GIS renders cannot be given
        // a `disabled` attribute from outside. Stops a second click from
        // starting a second exchange while the first is still in flight.
        <div
          className="absolute inset-0 cursor-progress rounded-full bg-[var(--cp-canvas)]/50"
          aria-hidden="true"
        />
      )}
    </div>
  );
}
